import { Box, Divider, Grid, Typography } from "@mui/material";

import { useTranslation } from "next-i18next";

import React from "react";

import { InferType, q } from "groqd";

import I18nLink from "../../../../components/Atoms/I18nLink";
import { baseArticleQuery } from "../../../../lib/sanity/queries/helpers";
import { runQuery } from "../../../../lib/sanityClient";
import { BaseArticleSchema } from "../../../../types/sanity-data/article";
import {
    ExcerptFieldSchema,
    PathFieldSchema
} from "../../../../types/sanity-data/derivedFields";

export const ArticlePropsSchema = BaseArticleSchema.pick({
    title: true,
    publishedAt: true,
    slug: true
})
    .merge(ExcerptFieldSchema)
    .merge(PathFieldSchema);

export const articleRollQuery = baseArticleQuery
    .filter('!(_id in path("drafts.**"))')
    .filter('coalesce(language, "en") == $locale')
    .order("publishedAt desc")
    .slice(0, 4)
    .grab$({
        publishedAt: q.string(),
        slug: [
            "slug",
            q.object({
                current: q.string(),
                _type: q.literal("slug")
            })
        ],
        title: q.string(),
        excerpt: [
            `array::join(string::split((pt::text(body)), "")[0..255], "") + "..."`,
            q.string()
        ],
        path: [`"/articles/" + slug.current`, q.string()]
    });

export const articleRollFetch = async (params: Record<string, unknown> = {}) =>
    runQuery(articleRollQuery, params).then((data) =>
        articleRollQuery.schema.parse(data)
    );

export type RollArticles = InferType<typeof articleRollQuery.schema>;

interface Props {
    articles: RollArticles;
}

const Index = ({ articles }: Props) => {
    const { t } = useTranslation();

    return (
        <Box mt={4} mb={2} data-testid="articleollSection">
            <Grid
                container
                rowSpacing={0}
                columnSpacing={2}
                direction="row"
                justifyContent="center"
                alignItems="center"
                data-testid="articleRollList"
            >
                {articles &&
                    articles.map((item) => (
                        <Grid
                            item
                            xs={12}
                            md={10}
                            lg={8}
                            key={item.slug.current}
                            data-testid="articleRollItem"
                        >
                            <Typography
                                variant="h3"
                                component="h4"
                                color="primary"
                                align="center"
                                mb={2}
                                sx={{
                                    a: {
                                        color: (theme) => theme.palette.primary.dark
                                    }
                                }}
                                data-testid="articleRollItemTitle"
                            >
                                {item.slug ? (
                                    <I18nLink href={item.path}>{item.title}</I18nLink>
                                ) : (
                                    item.title
                                )}
                            </Typography>

                            {item.publishedAt && (
                                <Typography
                                    variant="h4"
                                    component="h5"
                                    align="center"
                                    mt={2}
                                    mb={2}
                                    data-testid="articleRollItemDate"
                                >
                                    {t("common:dateFormatted", {
                                        val: new Date(item.publishedAt),
                                        formatParams: {
                                            val: {
                                                weekday: "long",
                                                year: "numeric",
                                                month: "long",
                                                day: "numeric"
                                            }
                                        }
                                    })}
                                </Typography>
                            )}

                            <Typography data-testid="articleRollItemExcerpt">
                                {item.excerpt}
                            </Typography>

                            <Divider
                                component="hr"
                                sx={{ marginTop: 2, marginBottom: 4 }}
                                light
                            />
                        </Grid>
                    ))}
            </Grid>
        </Box>
    );
};

export default Index;
